import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid'
import { TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { Pager } from 'components/components/Pager'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { useAppDispatch, useAppSelector } from 'store'
import { GridFilterActionState, initialState, setGridFilters } from 'store/grid'
import { fetchFilterAction } from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery } from 'utils/QueryHelpers'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { getIBTradingAccountFields } from '../IBTradingAccountFields'

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)

  return {
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
  }
}

interface Props {
  onChange: (version: TradingAccountDto, checked?: boolean) => void
  initialValue?: string | string[]
  isMultiSelect?: boolean
  introducingBrokerId: string
  hasGroupRebate?: boolean
  hasTradingAccountRebate?: boolean
  hasRebate?: boolean
  onClose: () => void
}

export const SelectIBTradingAccount = (props: Props): JSX.Element => {
  const {
    onChange,
    initialValue: activeTradingAccount,
    introducingBrokerId,
    isMultiSelect,
    hasGroupRebate,
    hasTradingAccountRebate,
    hasRebate,
    onClose,
  } = props
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [gridFilterState, setGridFilterState] = useState<GridFilterActionState>(
    getPaginationInitialState(initialState())
  )

  const [tradingAccounts, setTradingAccounts] = useState<TradingAccountDto[]>([])
  const { filters } = useMapStateToProps()
  const { fetchFilters } = useMapDispatchToProps()

  useEffect(() => {
    if (isEmpty(filters)) fetchFilters()
  }, [])

  useEffect(() => {
    fetchTradingAccounts()
  }, [
    gridFilterState.pageSize,
    gridFilterState.pageIndex,
    gridFilterState.take,
    tableData?.skipCount,
    gridFilterState.sort?.length,
  ])

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      if (tradingAccounts === undefined) return null
      let query = null
      if (filter) query = buildQuery(filter)
      if (gridFilterState.filter) query = buildQuery(gridFilterState.filter)

      const conditions: string[] = []

      if (hasGroupRebate !== undefined) {
        conditions.push(`HasGroupRebate == ${hasGroupRebate}`)
      }
      if (hasTradingAccountRebate !== undefined) {
        conditions.push(`HasTradingAccountRebate == ${hasTradingAccountRebate}`)
      }
      if (hasRebate !== undefined) {
        conditions.push(`HasRebate == ${hasRebate}`)
      }

      if (conditions.length > 0) {
        const rebateConditions = conditions.join(' AND ')
        return query ? `${query} AND ${rebateConditions}` : rebateConditions
      }

      return query
    },
    [gridFilterState.filter, hasGroupRebate, hasTradingAccountRebate, hasRebate]
  )

  const fetchTradingAccounts = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const search = params?.query !== undefined ? params.query : getQuery()
      const pageIndex = gridFilterState.pageIndex ?? 1
      const pageSize = gridFilterState.pageSize ?? 10
      const take = gridFilterState.take ?? 10
      const skip = gridFilterState.skip ?? 0
      const skipCount = tableData?.skipCount !== false

      const response = await partnershipsApiClient.getTradingAccountsByIntroducingBrokerId(
        introducingBrokerId,
        {
          search,
          pageIndex,
          pageSize,
          skipCount,
        }
      )

      if (response?.items) setTradingAccounts(response.items)
      else setTradingAccounts([])
      const total = response.totalResults

      setGridFilterState({ ...gridFilterState, pageIndex, pageSize, skip, take, total })
      setTableData({ ...tableData, meta: response })
    },
    [gridFilterState, tableData, hasGroupRebate, hasTradingAccountRebate, hasRebate]
  )

  const handleSkipCountChange = async (skipCount: boolean) =>
    setTableData({ ...tableData, skipCount })

  const handlePageSizeChange = async (pageSize: number) =>
    setGridFilterState({ ...gridFilterState, pageIndex: 1, pageSize, take: pageSize })

  const handleGridPageChange = async (pageIndex: number) =>
    setGridFilterState({ ...gridFilterState, pageIndex })

  return (
    <DialogContent size='3xl'>
      <DialogHeader>
        <DialogTitle>Select Trading Accounts</DialogTitle>
        <SelectFiltersFacade
          setGridFilters={(next) => setGridFilterState((prev) => ({ ...prev, ...next }))}
          filterFetch={fetchTradingAccounts}
          gridState={gridFilterState}
          filterFields={getIBTradingAccountFields()}
        />
      </DialogHeader>

      <TableStateContext.Provider value={[tableData, setTableData]}>
        <Grid
          {...gridFilterState}
          resizable
          data={tradingAccounts}
          total={gridFilterState.total}
          take={gridFilterState.take}
          skip={gridFilterState.skip}
          pageable
          pager={() => (
            <Pager
              onSkipCountChange={handleSkipCountChange}
              onPageChange={handleGridPageChange}
              onPageSizeChange={handlePageSizeChange}
              page={gridFilterState.pageIndex || 1}
              pageSize={gridFilterState.pageSize || 10}
              take={gridFilterState.take || 10}
              skip={gridFilterState.skip || 0}
            />
          )}
          style={{ zIndex: 0 }}
        >
          <GridColumn
            field='Select'
            width={180}
            cell={(gridCellProps: GridCellProps) => (
              <td data-test='select-column'>
                <Button
                  onClick={() =>
                    onChange(
                      gridCellProps.dataItem,
                      isMultiSelect
                        ? !activeTradingAccount?.includes(gridCellProps.dataItem.id)
                        : activeTradingAccount !== gridCellProps.dataItem.id
                    )
                  }
                  data-test='select-button'
                >
                  {isMultiSelect
                    ? activeTradingAccount?.includes(gridCellProps.dataItem.id)
                      ? 'Selected'
                      : 'Select'
                    : activeTradingAccount === gridCellProps.dataItem.id
                      ? 'Selected'
                      : 'Select'}
                </Button>
              </td>
            )}
          />
          <GridColumn title='Name' field='name' filter='text' />
          <GridColumn title='Wallet' field='wallet.name' filter='text' />
          <GridColumn title='Account Group' field='accountGroupType.name' filter='text' />
          <GridColumn title='Account Type' field='accountType.name' filter='text' />
          <GridColumn title='Currency' field='currency.id' filter='text' />
          <GridColumn title='Status' field='status.name' filter='text' />
          <GridColumn title='Country' field='country.name' filter='text' />
          <GridColumn title='Entity' field='tickmillCompany.name' filter='text' />
        </Grid>
      </TableStateContext.Provider>
      <DialogFooter>
        <Button variant='outline' onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}
